import React, { useContext, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { UserContext } from './UserContext';
import { getOrganizationInvitationDetails, getUser, loginUser, loginWithGoogle, registerUser } from './apiService';
import GoogleSignInButton from './GoogleSignInButton';
import { CheckGlyph, checkBulletSx, sectionCardSx } from './designSystem';
import { formatOrganizationRole } from './roleLabels';

const rolloutBenefits = [
  'District baseline for universal standards',
  'Classroom or lab profiles for different learning settings',
  'Prompt-based AI review rules that match instructional intent',
  'Invitations, reporting, and rollout readiness for school pilots',
];

function Register() {
  const { setUser } = useContext(UserContext);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [institutionType, setInstitutionType] = useState('district');
  const [deploymentMethod, setDeploymentMethod] = useState('chrome_enterprise');
  const [jobTitle, setJobTitle] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const inviteToken = new URLSearchParams(location.search).get('invite');
  const googleSignupReady = Boolean(organizationName.trim() && jobTitle.trim());

  useEffect(() => {
    if (!inviteToken) {
      return;
    }

    const loadInvite = async () => {
      try {
        const response = await getOrganizationInvitationDetails(inviteToken);
        setEmail(response.invitation.email || '');
        setOrganizationName(response.organization?.name || '');
        setInviteMessage(
          `This invitation is for ${response.invitation.email} to join ${response.organization?.name || 'a school workspace'} as ${formatOrganizationRole(response.invitation.role)}.`
        );
      } catch (error) {
        setErrorMessage(error.response?.data?.detail || 'Unable to load the invitation details.');
      }
    };

    loadInvite();
  }, [inviteToken]);

  const handleRegister = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const user = await registerUser({
        username,
        email,
        password,
        organization_name: organizationName,
        institution_type: institutionType,
        deployment_method: deploymentMethod,
        job_title: jobTitle,
      });
      const tokenResponse = await loginUser(username, password);
      const token = tokenResponse.access_token;
      localStorage.setItem('token', token);
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      setUser({ username, email, subscription: user.subscription });
      navigate(inviteToken ? `/invite/${inviteToken}` : '/');
      window.location.reload();
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || 'Unable to create the administrator account.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async (credential) => {
    try {
      setLoading(true);
      setErrorMessage('');
      const tokenResponse = await loginWithGoogle({
        credential,
        mode: 'signup',
        username,
        organization_name: organizationName,
        institution_type: institutionType,
        deployment_method: deploymentMethod,
        job_title: jobTitle,
      });
      const token = tokenResponse.access_token;
      localStorage.setItem('token', token);
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      const updatedUser = await getUser();
      setUser(updatedUser);
      navigate(inviteToken ? `/invite/${inviteToken}` : '/');
      window.location.reload();
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || 'Unable to continue with Google.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      sx={{
        py: { xs: 5, md: 8 },
        minHeight: { md: 'calc(100vh - 220px)' },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box sx={{ maxWidth: 1120, mx: 'auto', width: '100%' }}>
        <Card sx={{ ...sectionCardSx, overflow: 'hidden', borderRadius: '24px' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '0.98fr 1.02fr' } }}>
            <CardContent sx={{ p: { xs: 4, md: 6 } }}>
              <Box component="form" onSubmit={(event) => { event.preventDefault(); handleRegister(); }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h4" sx={{ color: '#0B2E43' }}>Create your workspace</Typography>
                  <Typography sx={{ color: '#52677A', mt: 0.75 }}>
                    One administrator account sets up the whole school or district.
                  </Typography>
                </Box>

                {inviteToken && (
                  <Alert severity="info">
                    Create this account with the same email address that received the school invitation so it can be accepted immediately afterward.
                  </Alert>
                )}

                {inviteMessage && <Alert severity="success">{inviteMessage}</Alert>}

                {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

                <TextField
                  label="School or district name"
                  value={organizationName}
                  onChange={(event) => setOrganizationName(event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Administrator username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Your role"
                  value={jobTitle}
                  onChange={(event) => setJobTitle(event.target.value)}
                  fullWidth
                />
                <TextField
                  label="School email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  fullWidth
                />
                <TextField
                  select
                  label="Institution type"
                  value={institutionType}
                  onChange={(event) => setInstitutionType(event.target.value)}
                  fullWidth
                >
                  <MenuItem value="district">District office</MenuItem>
                  <MenuItem value="school">Single school</MenuItem>
                  <MenuItem value="charter_network">Charter network</MenuItem>
                  <MenuItem value="library">Library or media program</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Primary rollout path"
                  value={deploymentMethod}
                  onChange={(event) => setDeploymentMethod(event.target.value)}
                  fullWidth
                >
                  <MenuItem value="chrome_enterprise">Chrome Enterprise / Google Admin</MenuItem>
                  <MenuItem value="intune">Microsoft Intune</MenuItem>
                  <MenuItem value="jamf">Jamf School / Jamf Pro</MenuItem>
                  <MenuItem value="manual">Manual pilot rollout</MenuItem>
                </TextField>
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  fullWidth
                />
                <Button type="submit" variant="contained" color="primary" size="large" disabled={loading}>
                  {loading ? 'Creating Workspace...' : 'Create Workspace'}
                </Button>

                <GoogleSignInButton
                  text="signup_with"
                  onCredential={handleGoogleRegister}
                  helperText="Use Google to create or link your administrator account without choosing a password first."
                  disabled={!googleSignupReady}
                  disabledMessage="Add the school or district name and your role first so BlockWebAI can create the workspace correctly."
                />

                <Typography sx={{ color: '#52677A', fontSize: '0.95rem' }}>
                  Already have an account?{' '}
                  <RouterLink to={inviteToken ? `/login?invite=${inviteToken}` : '/login'} style={{ color: '#0E4D6E', fontWeight: 700 }}>
                    Sign in
                  </RouterLink>
                </Typography>
              </Stack>
              </Box>
            </CardContent>

            <Box
              sx={{
                position: 'relative',
                p: { xs: 4, md: 6 },
                background:
                  'radial-gradient(circle at 85% -10%, rgba(19,148,127,0.4), transparent 46%), radial-gradient(circle at -10% 110%, rgba(14,77,110,0.65), transparent 52%), linear-gradient(160deg, #0B2E43 0%, #11425F 100%)',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage:
                    'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
                  backgroundSize: '44px 44px',
                  maskImage: 'radial-gradient(ellipse at 80% 20%, black 30%, transparent 78%)',
                  pointerEvents: 'none',
                }}
              />
              <Box sx={{ position: 'relative' }}>
                <Typography sx={{ color: '#7EE8D6', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.14em', textTransform: 'uppercase', mb: 2 }}>
                  Before devices go live
                </Typography>
                <Typography variant="h3" sx={{ mb: 2, fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
                  Set the foundation first.
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.68)', lineHeight: 1.8 }}>
                  Once your workspace is created, define a universal policy baseline, add classroom exceptions, and prepare your allowlists before deployment begins.
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 4 }}>
                  {rolloutBenefits.map((item) => (
                    <Stack key={item} direction="row" spacing={1.5} alignItems="flex-start">
                      <Box sx={{ ...checkBulletSx, bgcolor: 'rgba(19,148,127,0.24)', border: '1px solid rgba(19,148,127,0.45)' }}>
                        <CheckGlyph color="#7EE8D6" />
                      </Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.74)', fontSize: '0.95rem', lineHeight: 1.6 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>
    </Container>
  );
}

export default Register;
